/**
 * AllocationProcessor - Handles all allocation calculations for Roster feature
 * 
 * This service extracts the complex allocation calculation logic from the main component,
 * providing optimized, cached calculations for member outcome allocations.
 * 
 * Key responsibilities:
 * - Calculate monthly allocations from date ranges
 * - Process member outcome allocations with caching
 * - Handle both new calendar format and legacy project-relative format
 * - Optimize performance with memoization
 * - Skip cost estimation roles from calculations
 */

import dayjs from 'dayjs';
import { EMPTY_MONTHLY_ALLOCATIONS, safeString } from '../utils/rosterCalculations';

class AllocationProcessor {
  constructor() {
    this.memberOutcomeAllocationsCache = new Map();
    this.lastOutcomeAllocationsHash = '';
    this.calculationStats = {
      cacheHits: 0,
      cacheMisses: 0,
      calculations: 0
    };
  }

  /**
   * Calculate monthly allocations from dateRanges
   * Handles both new calendar format and legacy project-relative format
   */
  calculateMonthlyAllocationsFromDateRanges(dateRanges, projectStartMonth = null) {
    const monthlyAllocations = [...EMPTY_MONTHLY_ALLOCATIONS];
    
    if (!dateRanges || !Array.isArray(dateRanges)) {
      return monthlyAllocations;
    }
    
    dateRanges.forEach(range => {
      let startMonth, endMonth;
      
      // Simplified format detection - prioritize new calendar format
      const isNewCalendarFormat = (
        range.timelineVersion === '3.0' || // Explicit new format marker
        range.timelineVersion === '2.0' || // Also treat v2.0 as calendar format
        !projectStartMonth || // No project context means use direct calendar months
        (range.startMonth >= 1 && range.startMonth <= 12 && 
         range.endMonth >= 1 && range.endMonth <= 12 &&
         range.startMonth <= range.endMonth &&
         !range.useProjectRelative) // Not explicitly marked as project-relative
      );
      
      if (isNewCalendarFormat) {
        // New format: Direct calendar months (1-12) - use as-is
        startMonth = Math.max(1, Math.min(12, range.startMonth || 1));
        endMonth = Math.max(startMonth, Math.min(12, range.endMonth || startMonth));
        
        if (process.env.NODE_ENV === 'development' && range.timelineVersion) {
          console.log(`AllocationProcessor: Using calendar format (${range.timelineVersion}) for range: ${startMonth}-${endMonth}`);
        }
      } else {
        // Legacy format: Project-relative months - convert to calendar months
        if (!projectStartMonth) {
          console.warn('AllocationProcessor: Project-relative format detected but no projectStartMonth provided, using direct months');
          startMonth = Math.max(1, Math.min(12, range.startMonth || 1));
          endMonth = Math.max(startMonth, Math.min(12, range.endMonth || startMonth));
        } else {
          const projectStart = dayjs(projectStartMonth + '-01');
          const startDate = projectStart.add((range.startMonth || 1) - 1, 'month');
          const endDate = projectStart.add((range.endMonth || 1) - 1, 'month');
          startMonth = startDate.month() + 1; // Convert to 1-based calendar month
          endMonth = endDate.month() + 1;
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`AllocationProcessor: Converting project-relative ${range.startMonth}-${range.endMonth} to calendar ${startMonth}-${endMonth} (project start: ${projectStartMonth})`);
          }
        }
      }
      
      const allocation = Math.max(0, Math.min(100, range.allocation || 0));
      
      // Apply allocation to calendar months
      for (let month = startMonth; month <= endMonth; month++) {
        const monthIndex = month - 1; // Convert to 0-based index
        if (monthIndex >= 0 && monthIndex < 12) {
          monthlyAllocations[monthIndex] += allocation;
        }
      }
    });
    
    return monthlyAllocations;
  }

      /**
   * Get member outcome allocations with caching
   * This is the main method that processes all allocations for a specific member
   */
  getMemberOutcomeAllocations(memberId, outcomeAllocations, roster) {
    this.calculationStats.calculations++;
    
    // Create a simple hash of outcome allocations to detect changes
    const currentHash = `${outcomeAllocations.length}-${JSON.stringify(outcomeAllocations.map(o => o.outcome_id)).slice(0, 100)}`;
    
    // Clear cache if outcome allocations changed
    if (currentHash !== this.lastOutcomeAllocationsHash) {
      this.memberOutcomeAllocationsCache.clear();
      this.lastOutcomeAllocationsHash = currentHash;
      console.log('AllocationProcessor: Cleared cache due to outcome allocations change');
    }
    
    // Return cached result if available
    if (this.memberOutcomeAllocationsCache.has(memberId)) {
      this.calculationStats.cacheHits++;
      return this.memberOutcomeAllocationsCache.get(memberId);
    }
    
    this.calculationStats.cacheMisses++;
    
    // Debug logging for specific member
    const member = roster.find(m => m.id === memberId);
    const memberName = member?.name || 'Unknown';
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`📊 AllocationProcessor: Processing allocations for member ${memberName} (${memberId})`);
      console.log(`📊 AllocationProcessor: Available outcome allocations:`, outcomeAllocations.length);
      
      // Debug the first few allocations to see their structure
      if (outcomeAllocations.length > 0) {
        console.log(`🔍 AllocationProcessor: Sample allocation structures:`);
        outcomeAllocations.slice(0, 3).forEach((allocation, idx) => {
          console.log(`  [${idx}]:`, {
            outcome_id: allocation.outcome_id,
            outcome_name: allocation.outcome_name,
            member_id: allocation.member_id,
            has_staffed_roles: !!allocation.staffed_roles,
            staffed_roles_count: allocation.staffed_roles?.length || 0,
            is_flat_structure: !allocation.staffed_roles && !!allocation.member_id
          });
        });
      }
    }
    
    const memberOutcomes = {};
    
    // Group outcomes by name first to handle duplicates
    const outcomesByName = new Map();
    
    outcomeAllocations.forEach(outcome => {
      const outcomeName = safeString(outcome.outcome_name);
      if (!outcomesByName.has(outcomeName)) {
        outcomesByName.set(outcomeName, []);
      }
      outcomesByName.get(outcomeName).push(outcome);
    });
    
    // Process each unique outcome name only once
    outcomesByName.forEach((outcomes, outcomeName) => {
      const representativeOutcome = outcomes[0]; // Use first outcome as representative
      const outcomeData = {
        outcome_id: representativeOutcome.outcome_id,
        outcome_name: outcomeName,
        staffed: [...EMPTY_MONTHLY_ALLOCATIONS],
        applications: [],
        // Store project start information for debugging
        project_start_month: representativeOutcome.start_date ? 
          dayjs(representativeOutcome.start_date).format('YYYY-MM') : 
          representativeOutcome.project_start_month
      };
      
      // Collect all applications from all instances of this outcome
      const allApplications = new Set();
      outcomes.forEach(outcome => {
        if (Array.isArray(outcome.applications)) {
          outcome.applications.forEach(app => allApplications.add(safeString(app)));
        }
      });
      outcomeData.applications = Array.from(allApplications);
      
      let hasData = false;
      
      // Collect all roles for this member across all instances of this outcome
      const memberRoles = [];
      outcomes.forEach(outcome => {
        // Handle the actual data structure we're receiving
        // The allocations are flat objects with member_id directly on them, not nested in staffed_roles
        if (outcome.member_id === memberId) {
          // Debug member ID matching
          if (process.env.NODE_ENV === 'development' && memberName === 'Aaron Armitage') {
            console.log(`🔍 AllocationProcessor: Found direct match - allocation member_id '${outcome.member_id}' matches target '${memberId}'`);
          }
          
          // Convert the flat allocation object to a role-like structure
          const role = {
            member_id: outcome.member_id,
            role_name: outcome.role_name || 'Unknown Role',
            milestone_name: outcome.milestone_name || 'Unknown Milestone',
            allocation: outcome.allocation || 0,
            monthly_allocations: outcome.monthly_allocations || null,
            dateRanges: outcome.dateRanges || null,
            company: outcome.company || 'CA',
            location: outcome.location || 'US',
            function: outcome.function || 'Other',
            rate: outcome.rate || 0
          };
          
          // CRITICAL: Skip Cost Estimation roles when calculating member allocations
          if (role.milestone_name && role.milestone_name.toLowerCase().includes('cost estimation')) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`📊 AllocationProcessor: Skipping Cost Estimation role for member ${memberName} in ${outcomeName}: ${role.role_name}`);
            }
            return; // Skip this role entirely
          }
          
          memberRoles.push(role);
          if (process.env.NODE_ENV === 'development') {
            console.log(`📊 AllocationProcessor: Added direct role for member ${memberName} in ${outcomeName}: ${role.role_name} (${role.milestone_name})`);
          }
        }
        
        // Also check if there are nested staffed_roles (fallback for different data formats)
        if (outcome.staffed_roles && Array.isArray(outcome.staffed_roles)) {
          outcome.staffed_roles.forEach(role => {
            // Debug member ID matching
            if (process.env.NODE_ENV === 'development' && memberName === 'Aaron Armitage') {
              console.log(`🔍 AllocationProcessor: Checking nested role member_id '${role.member_id}' vs target '${memberId}' (${typeof role.member_id} vs ${typeof memberId})`);
            }
            
            if (role.member_id === memberId) {
              // CRITICAL: Skip Cost Estimation roles when calculating member allocations
              if (role.milestone_name && role.milestone_name.toLowerCase().includes('cost estimation')) {
                if (process.env.NODE_ENV === 'development') {
                  console.log(`📊 AllocationProcessor: Skipping Cost Estimation role for member ${memberName} in ${outcomeName}: ${role.role_name}`);
                }
                return; // Skip this role entirely
              }
              
              // Add all roles - don't deduplicate based on name, as same role name in different milestones should be summed
              memberRoles.push(role);
              if (process.env.NODE_ENV === 'development') {
                console.log(`📊 AllocationProcessor: Added nested role for member ${memberName} in ${outcomeName}: ${role.role_name} (${role.milestone_name})`);
              }
            }
          });
        }
      });
      
      // Process all roles for this member in this outcome
      memberRoles.forEach(role => {
        // Always prioritize dateRanges calculation over potentially stale monthly_allocations
        if (role.dateRanges && Array.isArray(role.dateRanges) && role.dateRanges.length > 0) {
          // Calculate from dateRanges using the project start month we determined above
          const calculated = this.calculateMonthlyAllocationsFromDateRanges(role.dateRanges, outcomeData.project_start_month);
          calculated.forEach((val, idx) => {
            const allocation = Math.max(0, Math.min(100, val || 0));
            outcomeData.staffed[idx] += allocation;
          });
        } else if (role.monthly_allocations && Array.isArray(role.monthly_allocations)) {
          // Fallback to pre-calculated monthly_allocations only if no dateRanges
          console.warn(`AllocationProcessor: Using fallback monthly_allocations for ${role.role_name} - dateRanges not available`);
          role.monthly_allocations.forEach((val, idx) => {
            if (idx < 12) {
              const allocation = Math.max(0, Math.min(100, val || 0));
              outcomeData.staffed[idx] += allocation;
            }
          });
        } else {
          // Last resort: create allocations based on role.allocation across all months
          const allocation = Math.max(0, Math.min(100, role.allocation || 0));
          const monthlyAllocations = Array(12).fill(allocation);
          monthlyAllocations.forEach((val, idx) => {
            outcomeData.staffed[idx] += val;
          });
        }
        hasData = true;
      });
      
      if (hasData) {
        // Use outcome_name as key to match how it's accessed in the UI
        memberOutcomes[outcomeName] = outcomeData;
      }
    });
    
    // Debug logging for results
    if (process.env.NODE_ENV === 'development' && Object.keys(memberOutcomes).length > 0) {
      console.log(`📊 AllocationProcessor: Member ${memberName} final allocations:`, Object.keys(memberOutcomes).length, 'outcomes');
      Object.values(memberOutcomes).forEach(outcome => {
        const totalAllocation = outcome.staffed.reduce((sum, val) => sum + val, 0);
        console.log(`📊 AllocationProcessor: - ${outcome.outcome_name}: ${totalAllocation}% total allocation`);
      });
    }
    
    // Cache the result
    this.memberOutcomeAllocationsCache.set(memberId, memberOutcomes);
    return memberOutcomes;
  }

  /**
   * Process outcome allocations data
   * Validates and normalizes the allocation data structure
   */
  processOutcomeAllocations(allocationsData) {
    if (!Array.isArray(allocationsData)) {
      console.warn('AllocationProcessor: Invalid allocations data format, expected array');
      return [];
    }

    return allocationsData.map(outcome => {
      // Ensure required fields exist
      const processedOutcome = {
        outcome_id: outcome.outcome_id || outcome.id,
        outcome_name: safeString(outcome.outcome_name || outcome.name),
        applications: Array.isArray(outcome.applications) ? outcome.applications : [],
        staffed_roles: Array.isArray(outcome.staffed_roles) ? outcome.staffed_roles : [],
        start_date: outcome.start_date,
        project_start_month: outcome.project_start_month,
        ...outcome
      };

      // Validate and process staffed roles
      processedOutcome.staffed_roles = processedOutcome.staffed_roles.map(role => ({
        member_id: role.member_id,
        role_name: safeString(role.role_name || role.name),
        milestone_name: safeString(role.milestone_name),
        allocation: Math.max(0, Math.min(100, role.allocation || 0)),
        monthly_allocations: Array.isArray(role.monthly_allocations) ? 
          role.monthly_allocations.map(val => Math.max(0, Math.min(100, val || 0))) : 
          null,
        dateRanges: Array.isArray(role.dateRanges) ? role.dateRanges : null,
        ...role
      }));

      return processedOutcome;
    });
  }

  /**
   * Calculate member summary statistics
   */
  calculateMemberSummary(roster, outcomeAllocations) {
    const summary = {
      totalMembers: roster.length,
      totalAssigned: 0,
      totalUnassigned: 0,
      overAllocated: 0,
      fullyAllocated: 0,
      underAllocated: 0,
      hasAllocationData: outcomeAllocations.length > 0
    };

    if (outcomeAllocations.length === 0) {
      summary.totalUnassigned = roster.length;
      return summary;
    }

    // Get all assigned member IDs
    const assignedMemberIds = new Set();
    outcomeAllocations.forEach(outcome => {
      if (outcome.staffed_roles) {
        outcome.staffed_roles.forEach(role => {
          if (role.member_id) {
            assignedMemberIds.add(role.member_id);
          }
        });
      }
    });

    summary.totalAssigned = assignedMemberIds.size;
    summary.totalUnassigned = roster.length - assignedMemberIds.size;

    // Calculate allocation status for assigned members
    assignedMemberIds.forEach(memberId => {
      const memberOutcomes = this.getMemberOutcomeAllocations(memberId, outcomeAllocations, roster);
      
      // Calculate total monthly allocations
      const monthlyTotals = [...EMPTY_MONTHLY_ALLOCATIONS];
      Object.values(memberOutcomes).forEach(outcome => {
        outcome.staffed.forEach((val, idx) => {
          monthlyTotals[idx] += val;
        });
      });

      const maxAllocation = Math.max(...monthlyTotals);
      
      if (maxAllocation > 100) {
        summary.overAllocated++;
      } else if (maxAllocation === 100) {
        summary.fullyAllocated++;
      } else if (maxAllocation > 0) {
        summary.underAllocated++;
      }
    });

    return summary;
  }

  /**
   * Clear allocation cache
   */
  clearCache() {
    console.log('🧹 AllocationProcessor: Clearing allocation cache');
    this.memberOutcomeAllocationsCache.clear();
    this.lastOutcomeAllocationsHash = '';
    this.calculationStats = {
      cacheHits: 0,
      cacheMisses: 0,
      calculations: 0
    };
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      ...this.calculationStats,
      cacheSize: this.memberOutcomeAllocationsCache.size,
      cacheEfficiency: this.calculationStats.calculations > 0 ? 
        (this.calculationStats.cacheHits / this.calculationStats.calculations * 100).toFixed(1) + '%' : 
        '0%'
    };
  }

  /**
   * Force recalculation for specific member
   */
  forceRecalculateForMember(memberId) {
    this.memberOutcomeAllocationsCache.delete(memberId);
    console.log(`AllocationProcessor: Cleared cache for member ${memberId}`);
  }

  /**
   * Batch process multiple members
   */
  batchProcessMembers(memberIds, outcomeAllocations, roster) {
    const results = new Map();
    
    memberIds.forEach(memberId => {
      const allocations = this.getMemberOutcomeAllocations(memberId, outcomeAllocations, roster);
      results.set(memberId, allocations);
    });

    return results;
  }
}

export { AllocationProcessor };